import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, CheckCircle2, RefreshCw, Loader2, UserPlus, Filter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ErrorRow {
  id: string;
  user_email: string | null;
  user_id: string | null;
  error_type: string;
  error_code: string | null;
  error_message: string | null;
  resource_type: string | null;
  attempted_action: string | null;
  page_path: string | null;
  resolved_at: string | null;
  created_at: string;
  metadata: { source?: string | null } | null;
}

type SourceFilter = "any" | "global-interceptor" | "manual";

const AdminErrorLog = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"unresolved" | "all">("unresolved");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("any");
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkEmail, setBulkEmail] = useState("");
  const [bulkPath, setBulkPath] = useState("");
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkPreviewCount, setBulkPreviewCount] = useState<number | null>(null);
  const [rlsRecentOnly, setRlsRecentOnly] = useState(false);

  const markAsApplicant = async (row: ErrorRow) => {
    if (!row.user_id) {
      toast.error("Cannot approve: no user_id on this error row");
      return;
    }
    setApprovingId(row.id);
    const { data, error } = await supabase.functions.invoke("admin-create-provider-applicant", {
      body: { target_user_id: row.user_id },
    });
    setApprovingId(null);
    if (error) {
      toast.error("Could not mark as applicant", { description: error.message });
      return;
    }
    if (data?.already === "provider") {
      toast.info("User is already an approved provider");
    } else if (data?.already === "pending") {
      toast.info("User already has a pending provider application");
    } else {
      toast.success("Marked as provider applicant", {
        description: "They can now submit credentials and W-9 info.",
      });
    }
    // Auto-resolve the unblocking error row
    if (user && !row.resolved_at) {
      await supabase
        .from("admin_error_log")
        .update({ resolved_at: new Date().toISOString(), resolved_by: user.id })
        .eq("id", row.id);
      fetchRows();
    }
  };

  const fetchRows = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("admin_error_log")
      .select("id, user_email, user_id, error_type, error_code, error_message, resource_type, attempted_action, page_path, resolved_at, created_at, metadata")
      .order("created_at", { ascending: false })
      .limit(100);
    if (filter === "unresolved") query = query.is("resolved_at", null);
    const { data, error } = await query;
    if (error) {
      toast.error("Failed to load error log", { description: error.message });
    } else {
      setRows((data ?? []) as ErrorRow[]);
    }
    setLoading(false);
  }, [filter]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const markResolved = async (id: string) => {
    if (!user) return;
    setResolvingId(id);
    const { error } = await supabase
      .from("admin_error_log")
      .update({ resolved_at: new Date().toISOString(), resolved_by: user.id })
      .eq("id", id);
    setResolvingId(null);
    if (error) {
      toast.error("Could not mark as resolved", { description: error.message });
    } else {
      toast.success("Marked as resolved");
      fetchRows();
    }
  };

  const buildBulkFilter = (q: any) => {
    const email = bulkEmail.trim();
    const path = bulkPath.trim();
    if (email) q = q.eq("user_email", email);
    if (path) q = q.eq("page_path", path);
    return q;
  };

  const previewBulk = async () => {
    if (!bulkEmail.trim() && !bulkPath.trim()) {
      toast.error("Enter an email or page path to preview");
      return;
    }
    let q = supabase
      .from("admin_error_log")
      .select("id", { count: "exact", head: true })
      .is("resolved_at", null);
    q = buildBulkFilter(q);
    const { count, error } = await q;
    if (error) {
      toast.error("Preview failed", { description: error.message });
      setBulkPreviewCount(null);
      return;
    }
    setBulkPreviewCount(count ?? 0);
  };

  const runBulkResolve = async () => {
    if (!user) return;
    if (!bulkEmail.trim() && !bulkPath.trim()) {
      toast.error("Enter an email or page path");
      return;
    }
    setBulkRunning(true);
    let q = supabase
      .from("admin_error_log")
      .update({ resolved_at: new Date().toISOString(), resolved_by: user.id })
      .is("resolved_at", null);
    q = buildBulkFilter(q);
    const { data, error } = await q.select("id");
    setBulkRunning(false);
    if (error) {
      toast.error("Bulk resolve failed", { description: error.message });
      return;
    }
    const n = data?.length ?? 0;
    toast.success(`Resolved ${n} row${n === 1 ? "" : "s"}`, {
      description: [bulkEmail.trim() && `email: ${bulkEmail.trim()}`, bulkPath.trim() && `path: ${bulkPath.trim()}`]
        .filter(Boolean)
        .join(" · "),
    });
    setBulkOpen(false);
    setBulkEmail("");
    setBulkPath("");
    setBulkPreviewCount(null);
    fetchRows();
  };

  const matchesSource = (r: ErrorRow): boolean => {
    if (sourceFilter === "any") return true;
    const src = r.metadata?.source ?? null;
    if (sourceFilter === "global-interceptor") return src === "global-interceptor";
    return src !== "global-interceptor"; // "manual" = anything not tagged by interceptor
  };

  const last24hCutoff = Date.now() - 24 * 60 * 60 * 1000;
  const visibleRows = rows
    .filter(matchesSource)
    .filter((r) =>
      rlsRecentOnly
        ? r.error_type === "rls_violation" && new Date(r.created_at).getTime() >= last24hCutoff
        : true,
    );
  const unresolvedCount = visibleRows.filter((r) => !r.resolved_at).length;
  const recentRlsCount = rows.filter(
    (r) => r.error_type === "rls_violation" && new Date(r.created_at).getTime() >= last24hCutoff,
  ).length;

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <ShieldAlert size={18} className="text-amber-600" />
          <CardTitle className="text-base">User Permission Errors</CardTitle>
          {filter === "unresolved" && unresolvedCount > 0 && (
            <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
              {unresolvedCount} open
            </Badge>
          )}
          {recentRlsCount > 0 && (
            <button
              type="button"
              onClick={() => setRlsRecentOnly((v) => !v)}
              title={
                rlsRecentOnly
                  ? "Showing only RLS rejections from the last 24h — click to clear"
                  : "Click to filter to RLS rejections from the last 24h"
              }
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                rlsRecentOnly
                  ? "bg-rose-600 text-white border-rose-700 hover:bg-rose-700"
                  : "bg-rose-100 text-rose-800 border-rose-300 hover:bg-rose-200 animate-pulse"
              }`}
            >
              <ShieldAlert size={11} />
              {recentRlsCount} RLS / 24h
              {rlsRecentOnly && <span className="ml-0.5 opacity-80">×</span>}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            <button
              onClick={() => setSourceFilter("any")}
              className={`px-3 py-1.5 transition-colors ${sourceFilter === "any" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            >
              Any source
            </button>
            <button
              onClick={() => setSourceFilter("global-interceptor")}
              className={`px-3 py-1.5 transition-colors border-l border-border ${sourceFilter === "global-interceptor" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            >
              Interceptor
            </button>
            <button
              onClick={() => setSourceFilter("manual")}
              className={`px-3 py-1.5 transition-colors border-l border-border ${sourceFilter === "manual" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            >
              Manual
            </button>
          </div>
          <div className="flex rounded-md border border-border overflow-hidden text-xs">
            <button
              onClick={() => setFilter("unresolved")}
              className={`px-3 py-1.5 transition-colors ${filter === "unresolved" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            >
              Unresolved
            </button>
            <button
              onClick={() => setFilter("all")}
              className={`px-3 py-1.5 transition-colors ${filter === "all" ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"}`}
            >
              All
            </button>
          </div>
          <Dialog
            open={bulkOpen}
            onOpenChange={(o) => {
              setBulkOpen(o);
              if (!o) setBulkPreviewCount(null);
            }}
          >
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" title="Bulk-resolve test/demo entries by email or page path">
                <Filter size={14} />
                Bulk resolve
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Bulk-resolve error log entries</DialogTitle>
                <DialogDescription>
                  Resolve all unresolved rows that match the email and/or page path below. Useful for clearing test traffic. Leave a field blank to ignore it; at least one is required.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div className="space-y-1.5">
                  <Label htmlFor="bulk-email" className="text-xs">User email (exact match)</Label>
                  <Input
                    id="bulk-email"
                    placeholder="patient@demo.com"
                    value={bulkEmail}
                    onChange={(e) => {
                      setBulkEmail(e.target.value);
                      setBulkPreviewCount(null);
                    }}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bulk-path" className="text-xs">Page path (exact match)</Label>
                  <Input
                    id="bulk-path"
                    placeholder="/dashboard"
                    value={bulkPath}
                    onChange={(e) => {
                      setBulkPath(e.target.value);
                      setBulkPreviewCount(null);
                    }}
                  />
                </div>
                {bulkPreviewCount !== null && (
                  <p className="text-xs text-muted-foreground">
                    {bulkPreviewCount === 0
                      ? "No matching unresolved rows."
                      : `Will resolve ${bulkPreviewCount} unresolved row${bulkPreviewCount === 1 ? "" : "s"}.`}
                  </p>
                )}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" size="sm" onClick={previewBulk} disabled={bulkRunning}>
                  Preview
                </Button>
                <Button
                  size="sm"
                  onClick={runBulkResolve}
                  disabled={bulkRunning || (!bulkEmail.trim() && !bulkPath.trim())}
                  className="gap-1.5"
                >
                  {bulkRunning ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Resolve matching
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading} className="gap-1.5">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && rows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            {filter === "unresolved" ? "No unresolved permission errors. " : "No errors logged yet."}
            {filter === "unresolved" && "🎉"}
          </div>
        ) : (
          <div className="space-y-2">
            {visibleRows.map((r) => (
              <div
                key={r.id}
                className={`p-3 rounded-md border ${r.resolved_at ? "border-border bg-muted/30" : "border-amber-200 bg-amber-50/50"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                        {r.error_type.replace(/_/g, " ")}
                      </Badge>
                      {r.resource_type && (
                        <span className="text-muted-foreground">
                          {r.resource_type}
                          {r.attempted_action && ` · ${r.attempted_action}`}
                        </span>
                      )}
                      <span className="text-muted-foreground">
                        {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="text-sm text-foreground font-medium truncate">
                      {r.user_email || (r.user_id ? `user ${r.user_id.slice(0, 8)}…` : "Unauthenticated")}
                    </p>
                    {r.error_message && (
                      <p className="text-xs text-muted-foreground line-clamp-2 font-mono">
                        {r.error_code && <span className="text-amber-700">[{r.error_code}] </span>}
                        {r.error_message}
                      </p>
                    )}
                    {r.page_path && (
                      <p className="text-[11px] text-muted-foreground">on {r.page_path}</p>
                    )}
                  </div>
                  {r.resolved_at ? (
                    <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 shrink-0 gap-1">
                      <CheckCircle2 size={12} /> Resolved
                    </Badge>
                  ) : (
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {r.resource_type === "provider_credentials" && r.user_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markAsApplicant(r)}
                          disabled={approvingId === r.id}
                          className="gap-1.5 bg-sky-50 hover:bg-sky-100 border-sky-300 text-sky-800"
                          title="Create a pending provider role-request so this user can submit credentials"
                        >
                          {approvingId === r.id ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <UserPlus size={14} />
                          )}
                          Mark as applicant
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => markResolved(r.id)}
                        disabled={resolvingId === r.id}
                      >
                        {resolvingId === r.id ? <Loader2 size={14} className="animate-spin" /> : "Mark resolved"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminErrorLog;
